import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosResponse } from 'axios';
import { Button } from 'components/universal/button';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { AsyncPaginate } from 'react-select-async-paginate';
import { toast } from 'react-toastify';
import { editUser, fetchDepartmentsPaginated } from 'services/registration';
import { APIErrors } from 'types/api';
import { employeesInterface } from 'views/employee-management';
import { DropdownIndicator } from '../addMultipleUser';
import { Input } from '../input';
import cls from './editUserData.module.scss';
import { schema } from './validation';

export interface EditUserDataValue {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    userIds: string;
    employee_position_name: string;
    department_id: string | number;
};

const defaultValues = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '000000000',
    userIds: '',
    employee_position_name: '',
    department_id: '',
};

export type EditUserRequest = {
    data: EditUserDataValue
}

interface EditUserDataFormFormInterface {
    user: employeesInterface[];
    workplaceId: number;
    onCancel: (boolean) => void;
}

export const EditUserDataForm = ({
    user,
    workplaceId,
    onCancel
}: EditUserDataFormFormInterface) => {
    const { t } = useTranslation();

    const [selectedDepartment, setSelectedDepartment] = useState<{ label: string; value: number } | null>(null);
    
    const {
        register,
        handleSubmit,
        setError,
        setValue,
        control,
        formState: { errors },
    } = useForm<EditUserDataValue>({
        resolver: yupResolver(schema),
        defaultValues: defaultValues
    });

    const onSubmit = (formObject: EditUserDataValue) => {
        const currentUser = user[0];
        const prepareData = formObject;
        
        prepareData['workplace_id'] = workplaceId;
        prepareData['has_elearning_permission'] = currentUser.has_elearning_permission;
        
        if (!prepareData.employee_position_name) {
            prepareData.employee_position_name = 'stanowisko';
        }

        editUserData.mutate({
            data: prepareData
        });
    };

    const editUserData = useMutation<
        AxiosResponse<{ message: string }>,
        AxiosError<APIErrors>,
        EditUserRequest
    >((payload) => editUser(payload), {
        onSuccess: (data) => {
            toast.success(t('employeeManagement.editSuccessMessage'));
            onCancel(true);
        },
        onError: (error) => {
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).forEach(([key, value]) => {
                    setError(key as keyof EditUserDataValue, {
                        message: value?.[0],
                    });
                });
            }
        },
    });

    useEffect(() => {
        const currentUser = user[0];
        
        if (currentUser) {
            setValue('first_name', currentUser.first_name || '');
            setValue('last_name', currentUser.last_name || '');
            setValue('phone', currentUser.phone || '000000000');
            setValue('userIds', String(currentUser.id));
            setValue('employee_position_name', currentUser.employee_position_name);
            if (currentUser.department_id) {
                setValue('department_id', currentUser.department_id);
                setSelectedDepartment({ label: currentUser.department?.name || '', value: currentUser.department_id });
            }
        }
    }, [user, setValue]);

    return (
        <>
            <h3 className={cls.modalHeader}>{t('employeeManagement.editUserData')}</h3>
            <form 
                className={cls.form}
                onSubmit={handleSubmit(onSubmit)} 
                noValidate
            >
                <input {...register('userIds')} type="hidden" />
                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="first_name"
                    name="first_name"
                    label={t('registration.firstName')}
                    error={t(errors.first_name?.message)}
                    maxLength={255}
                />
                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="last_name"
                    name="last_name"
                    label={t('registration.lastName')}
                    error={t(errors.last_name?.message)}
                    maxLength={255}
                />
                <Input
                    className={`${cls.input}`}
                    register={register}
                    id="employee_position_name"
                    name="employee_position_name"
                    label={t('registration.position')}
                    error={t(errors.employee_position_name?.message)}
                    maxLength={255}
                />
                <div className={`${cls.selectCustomWrapper} ${cls.input}`}>
                    <label htmlFor="department_id" className={cls.label}>
                        {t('registration.workplaceDepartments')}
                    </label>
                    <Controller
                        name="department_id"
                        control={control}
                        render={({ field }) => (
                            <AsyncPaginate
                                key={workplaceId}
                                className={errors.department_id ? 'error react-select-container' : 'react-select-container'}
                                classNamePrefix="react-select"
                                components={{ DropdownIndicator }}
                                placeholder=""
                                value={selectedDepartment}
                                loadOptions={async (search, _, { page }: { page: number }) => {
                                    const res = await fetchDepartmentsPaginated(workplaceId, page);
                                    return {
                                        options: res.data.map((d: any) => ({ label: d.name, value: d.id })),
                                        hasMore: res.current_page < res.last_page,
                                        additional: { page: page + 1 },
                                    };
                                }}
                                additional={{ page: 1 }}
                                onChange={(selected: { label: string; value: number } | null) => {
                                    setSelectedDepartment(selected);
                                    field.onChange(selected?.value ?? '');
                                }}
                                noOptionsMessage={() => t('registration.noDepartments')}
                                debounceTimeout={300}
                            />
                        )}
                    />
                    {errors.department_id && (
                        <p className={cls.errorBox}>{t(errors.department_id.message)}</p>
                    )}
                </div>
                <div className={cls.modalButtonWrapper}>
                    <Button className={cls.modalButton} styleType="border" type="button" onClick={onCancel}>
                        {t('employeeManagement.cancel')}
                    </Button>
                    <Button className={cls.modalButton} type="submit">
                        {t('employeeManagement.send2')}
                    </Button>
                </div>
            </form>
        </>
    );
};
